// Stage 201-X: Laughter pulse — per-isolate, per-turnId in-memory
// signal that one or more Laughter engrams have just fired.
//
// Written by useLaughterSubscriber the instant it enqueues a deposit
// (not when the deposit RESOLVES — the marker fires when the laugh is
// recognised, not when the network round-trip completes; this matches
// the witness-discovery framing). Read by WARP's BiteRow + WarpScene
// to apply the `is-laugh-fired` treatment.
//
// Memorylessness: the pulse is REPLACED (not appended) on each new
// turnId — a new turn wipes prior pulses. This mirrors the
// useGadflyTurn store and preserves the "the widget reflects the
// current turn only" property of WARP.

import { useSyncExternalStore } from 'react';

export interface LaughterPulse {
  /** The turnId that produced these pulses, or null if no pulse yet. */
  turnId: string | null;
  /** Bite indices within this turn that fired a Laughter engram. */
  firedBiteIndices: ReadonlyArray<number>;
  /** ISO-8601 of the most recent pulse (for ordering / debug). */
  lastFiredAt: string | null;
}

const EMPTY: LaughterPulse = {
  turnId: null,
  firedBiteIndices: [],
  lastFiredAt: null,
};

let snapshot: LaughterPulse = EMPTY;
const listeners = new Set<() => void>();

function publish(next: LaughterPulse): void {
  snapshot = next;
  for (const l of listeners) l();
}

/**
 * Called by useLaughterSubscriber when a bite has been recognised as
 * having fired a Laughter engram. If `turnId` differs from the current
 * snapshot's turnId, the store resets (per-turn ephemerality). Multiple
 * fires within the same turnId append biteIndex to the same snapshot.
 */
export function applyLaughterFired(input: {
  turnId: string;
  biteIndex: number;
}): void {
  const now = new Date().toISOString();
  if (snapshot.turnId !== input.turnId) {
    publish({
      turnId: input.turnId,
      firedBiteIndices: [input.biteIndex],
      lastFiredAt: now,
    });
    return;
  }
  if (snapshot.firedBiteIndices.includes(input.biteIndex)) return; // idempotent
  publish({
    ...snapshot,
    firedBiteIndices: [...snapshot.firedBiteIndices, input.biteIndex],
    lastFiredAt: now,
  });
}

/** Test-only: reset to EMPTY. */
export function _resetLaughterPulseForTest(): void {
  publish(EMPTY);
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
function getSnapshot(): LaughterPulse {
  return snapshot;
}

/** React hook — returns the current pulse, re-renders on change. */
export function useLaughterPulse(): LaughterPulse {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
